import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import { classNames } from "./shared";
import { safeHref } from "../lib/safe-href";

/**
 * Public attachment download chips (Wave A) — the ONE runtime renderer for
 * the `attachments` prop baked onto speaker/sponsor/agenda children (and
 * served live on slotting treatments). Entries are pre-filtered at
 * bake/serve time (showOnWebsite) and carry STABLE public URLs — never
 * presigns. Renders nothing for absent/empty/malformed props, so old baked
 * JSON and old per-event bundles stay unaffected either way.
 */
export type AttachmentChip = {
  name?: string;
  url?: string;
  contentType?: string;
  fileSize?: number;
};

export function parseAttachmentChips(raw: unknown): AttachmentChip[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (a): a is AttachmentChip =>
      !!a &&
      typeof a === "object" &&
      typeof (a as AttachmentChip).url === "string" &&
      ((a as AttachmentChip).url as string).length > 0 &&
      // PROTOCOL GATE. These urls are server-minted on every supported
      // surface, BUT `attachments` is an ordinary Craft.js prop persisted
      // verbatim in `websiteJson`, and the builder save accepts arbitrary
      // page JSON — so a tenant admin can plant `javascript:` via the API
      // and it would render to every public visitor. Drop such an entry
      // entirely rather than rendering an inert `#` chip that still looks
      // like a download. Same allow-list every other runtime href uses.
      isDownloadableUrl((a as AttachmentChip).url as string),
  );
}

/** http/https only — `mailto:`/`tel:` are in `safeHref`'s allow-list but are
 *  meaningless on a download chip, so this is deliberately stricter. */
function isDownloadableUrl(url: string): boolean {
  if (safeHref(url, "") === "") return false;
  try {
    const proto = new URL(url, "https://placeholder.com").protocol;
    return proto === "http:" || proto === "https:";
  } catch {
    return false;
  }
}

function formatSize(bytes: number | undefined): string | null {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function AttachmentChips({
  attachments,
  className,
}: {
  attachments: unknown;
  className?: string;
}) {
  const { currentColorPalette } = useWBTheme();
  const chips = parseAttachmentChips(attachments);
  if (chips.length === 0) return null;
  return (
    <div className={classNames("flex flex-wrap gap-2", className)}>
      {chips.map((chip, index) => {
        const size = formatSize(chip.fileSize);
        return (
          <a
            key={`${chip.url}-${index}`}
            href={safeHref(chip.url)}
            download
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-opacity hover:opacity-80"
            style={{
              borderColor: currentColorPalette.secondary,
              color: currentColorPalette.primary,
            }}
          >
            {/* Inline download glyph — the runtime bundles no icon library. */}
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 21h16" />
            </svg>
            <span className="truncate max-w-40">{chip.name || chip.url}</span>
            {size && <span style={{ opacity: 0.7 }}>{size}</span>}
          </a>
        );
      })}
    </div>
  );
}
